import { Box, FormControl, Typography } from "@mui/material";
import styles from "./MPCDocument.module.scss";
import {
  CustomMenuItem,
  CustomSelect,
} from "../../../common/StyledComponents/StyledFields";
import { useEffect, useState } from "react";
import { KeyboardArrowDown } from "@mui/icons-material";
import ErrorPopup from "../../../common/ErrorPopup";
import {
  apiRequest,
  catchErrorMessageHandler,
} from "../../../../infrastructure/backendService";
import toast from "react-hot-toast";
import constants from "../../../../config/constants.json";
import apiPath from "../../../../config/apiPaths.json";
import Pagination from "../../../common/pagination/pagination";
import Locales from "../../../../config/en-us.json";

const MPCDocument = () => {
  const MPCDocumentLocales = Locales.DASHBOARD.MPCDOCUMENTS;

  const [yearsList, setYearsList] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [MPCDocumentLists, setMPCDocumentsList] = useState<any[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  useEffect(() => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.MPCDocumentYears,
      includeToken: true,
      params: {
        sortBy: "year",
        page: 0,
        size: 100,
        direction: "desc",
      },
    })
      .then((res) => {
        const years = res.data.content;
        setYearsList(res.data.content);
        setSelectedYear(years[0]);
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  }, []);

  useEffect(() => {
    if (selectedYear) {
      apiRequest({
        method: "GET",
        endpoint: apiPath.MPCDocumentsByYear,
        includeToken: true,
        params: {
          page: pageNumber - 1,
          size: 10,
          year: selectedYear,
        },
      })
        .then((res) => {
          setMPCDocumentsList(res.data.content);
          setTotalRecords(res.data.totalElements);
        })
        .catch((err) => {
          console.log(err);
          toast.custom(
            <ErrorPopup
              showMessage={catchErrorMessageHandler(err)}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />,
            {
              duration: constants.TOAST_POPUP_DURATION,
            }
          );
        });
    }
  }, [selectedYear, pageNumber]);

  const handleFileClick = (fileUrl: string) => {
    if (fileUrl) {
      window.open(`${fileUrl}`, "_blank");
    }
  };

  const handleYearChange = (e: any) => {
    setSelectedYear(e.target.value);
    setPageNumber(1);
  };

  const renderYearsDropDown = (): React.ReactElement => {
    return (
      <FormControl className={styles.dropdownStyle} sx={{ width: "100%" }}>
        <CustomSelect
          IconComponent={KeyboardArrowDown}
          id="grouped-select"
          placeholder={MPCDocumentLocales.HEADER.SELECTYEAR}
          value={selectedYear}
          onChange={handleYearChange}
          MenuProps={{
            PaperProps: {
              sx: {
                border: "1px solid #F05802",
                "& ul": {
                  paddingTop: 0,
                  paddingBottom: 0,
                  overflowY: "auto",
                  minHeight: "auto",
                  maxHeight: "300px",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#F3C539",
                    borderRadius: "6px",
                  },
                },
              },
            },
          }}
        >
          {yearsList.map((item: number, index: number) => (
            <CustomMenuItem key={index} value={item}>
              {item}
            </CustomMenuItem>
          ))}
        </CustomSelect>
      </FormControl>
    );
  };

  const renderHeaders = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: "20px",
          mb: "20px",
        }}
      >
        <Typography className={styles.mpc_document_title}>
          {MPCDocumentLocales.HEADER.TITLE}
        </Typography>
        <Box>
          <Typography className={styles.select_year_text}>
            {MPCDocumentLocales.HEADER.SELECTYEAR}
          </Typography>
          {renderYearsDropDown()}
        </Box>
      </Box>
    );
  };

  const renderMOPDocumentTable = () => {
    return (
      <Box>
        {MPCDocumentLists?.length > 0 ? (
          <Box
            className={styles.table_container}
            sx={{ pl: "20px", pr: "15px" }}
          >
            <table className={styles.table}>
              <thead>
                <tr className={styles.table_heading_row}>
                  <th className={styles.table_heading}>
                    {MPCDocumentLocales.TABLEHEADERS.TITLE}
                  </th>
                  <th className={styles.table_heading}>
                    {MPCDocumentLocales.TABLEHEADERS.DESCRIPTION}
                  </th>
                  <th className={styles.table_heading}>
                    {MPCDocumentLocales.TABLEHEADERS.DATE}
                  </th>
                </tr>
              </thead>
              <tbody>
                {MPCDocumentLists?.map((document: any, index: number) => (
                  <tr key={index} className={styles.table_row}>
                    <td
                      className={`${styles.table_data} ${styles.document_name}`}
                      onClick={() => {
                        handleFileClick(document.fileUrl);
                      }}
                    >
                      {document.title}
                    </td>
                    <td className={styles.table_data}>
                      {document.description}
                    </td>
                    <td className={styles.table_data}>{document.uploadedOn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", mb: "20px" }}>
            <Typography className={styles.select_year_text}>
              {MPCDocumentLocales.NODATA}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderPagination = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          mr: "20px",
          py: "10px",
        }}
      >
        <Pagination
          items={totalRecords}
          itemsPerPage={10}
          setPageNumber={setPageNumber}
          pageNumber={pageNumber}
        />
      </Box>
    );
  };

  return (
    <Box className={styles.mpc_document_main_container} py={"15px"}>
      {renderHeaders()}
      {renderMOPDocumentTable()}
      {totalRecords > 10 && renderPagination()}
    </Box>
  );
};

export default MPCDocument;
